import { useState } from 'react';
import { AlertTriangle, CircleDollarSign, Plus } from 'lucide-react';
import { api, ApiError, type SmsCapacity } from '../lib/api';
import { Drawer } from './ui/Drawer';
import { Fact, FactSection } from './ui/Facts';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
import { formatNumber, formatPhone, formatUsd } from '../lib/format';

/**
 * Leasing another 10DLC number. Its own drawer and its own permission, because
 * it is the only action on these pages that commits recurring spend and consumes
 * a finite quota — and because releasing a number afterwards does not give it
 * back.
 *
 * The quota is checked before the button is offered rather than after it is
 * pressed. At the time of writing the account sits at 120 of 120, so the honest
 * thing for this panel to do is explain that and refuse.
 *
 * Mounted only while open, which is what lets the state below be initialised
 * once per opening rather than reset by an effect — and the client token is the
 * reason that matters.
 */
export function SmsAcquireDrawer({
  capacity,
  onClose,
  onAcquired,
}: {
  capacity: SmsCapacity | null | undefined;
  onClose: () => void;
  onAcquired: () => void;
}) {
  const quota = capacity?.phoneNumbers ?? null;
  const campaigns = capacity?.registrations ?? [];
  const atCeiling = quota !== null && quota.used >= quota.max;
  const spend = capacity?.spendLimits.find((l) => l.name === 'TEXT_MESSAGE_MONTHLY_SPEND_LIMIT');

  // Emptiest campaign first, which is the order the API returns them in.
  const [registrationId, setRegistrationId] = useState(
    () => campaigns[0]?.registrationId ?? '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  // One token per opening, not per click: it goes to AWS as the idempotency key,
  // so a double-submit resolves to the same lease instead of buying twice.
  const [clientToken] = useState(() => crypto.randomUUID());

  const acquire = async () => {
    setSaving(true);
    setError(null);
    try {
      const out = await api.acquireSmsNumber(registrationId, clientToken);
      setResult(out.phoneNumber ?? 'a new number');
      onAcquired();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <div className="min-w-0">
          <div className="text-sm font-semibold">Acquire a number</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Leases one more 10DLC number from AWS
          </p>
        </div>
      }
    >
      <div className="space-y-6">
        <FactSection title="Account ceiling">
          <Fact
            label="Phone numbers"
            value={
              quota ? (
                <span className={atCeiling ? 'text-destructive' : undefined}>
                  {formatNumber(quota.used)} of {formatNumber(quota.max)}
                </span>
              ) : (
                <span className="text-caution">could not be read</span>
              )
            }
          />
          {spend && (
            <Fact
              label="Monthly message spend cap"
              value={`${formatUsd(spend.enforced)} enforced · ${formatUsd(spend.max)} max`}
            />
          )}
          <Fact label="Cost of one number" value={`${formatUsd(1)} per month, ongoing`} />
        </FactSection>

        {atCeiling ? (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <p className="flex items-start gap-2 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <span>
                <span className="font-medium">The account is at its phone-number quota.</span> No
                further numbers can be leased until the <span className="font-mono">PHONE_NUMBERS</span>{' '}
                quota is raised.
              </span>
            </p>
            <p className="text-sm text-muted-foreground">
              That is a Service Quotas increase request against{' '}
              <span className="font-mono text-xs">AWS End User Messaging SMS</span>, reviewed by AWS
              rather than granted automatically — so it is worth requesting before you need the
              headroom, not when you run out.
            </p>
            <p className="text-sm text-muted-foreground">
              Freeing a number by removing an assignment does <span className="text-foreground">not</span>{' '}
              help: the number stays leased and still counts against this quota. Only releasing it
              does, and a released number cannot be recovered.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="sms-campaign">
                10DLC campaign
              </label>
              <Select
                id="sms-campaign"
                value={registrationId}
                onChange={(e) => setRegistrationId(e.target.value)}
                disabled={saving || campaigns.length === 0}
              >
                {campaigns.length === 0 && <option value="">No usable campaign registration</option>}
                {campaigns.map((c) => (
                  <option key={c.registrationId} value={c.registrationId}>
                    {c.registrationId} — {formatNumber(c.numbersHeld)} number
                    {c.numbersHeld === 1 ? '' : 's'}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                Completed campaign registrations only, emptiest first. Numbers sharing a campaign
                share its carrier throughput, and a campaign has its own number ceiling separate
                from the account quota.
              </p>
            </div>

            <p className="flex items-start gap-2 text-xs text-caution">
              <CircleDollarSign className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              This leases a real number and starts billing immediately. It arrives with two-way
              messaging off, so enable that on the number before expecting replies to reach the
              gateway.
            </p>
          </>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
        {result && (
          <p className="text-sm text-ok">
            Leased <span className="font-mono">{formatPhone(result)}</span>. It is now a spare, with
            two-way off.
          </p>
        )}

        {!atCeiling && (
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void acquire()}
              disabled={saving || registrationId === '' || result !== null}
            >
              <Plus className="h-3.5 w-3.5" />
              {saving ? 'Leasing…' : 'Lease one number'}
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
}
